import React from 'react';
import classNames from 'classnames';

/**
 * Card Component
 */
export const Card = ({ children, className = '', ...props }) => (
  <div
    className={classNames(
      'bg-gray-800 border border-gray-700 rounded-lg p-6',
      className
    )}
    {...props}
  >
    {children}
  </div>
);

/**
 * Button Component
 */
export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  className = '',
  ...props
}) => {
  const variants = {
    primary:
      'bg-purple-600 hover:bg-purple-700 text-white focus:ring-purple-500',
    secondary: 'bg-gray-700 hover:bg-gray-600 text-white focus:ring-gray-500',
    danger: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500',
    success: 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500',
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
  };

  return (
    <button
      className={classNames(
        'font-medium rounded-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed',
        variants[variant],
        sizes[size],
        className
      )}
      disabled={loading}
      {...props}
    >
      {loading ? (
        <span className="flex items-center space-x-2">
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          <span>Loading...</span>
        </span>
      ) : (
        children
      )}
    </button>
  );
};

/**
 * Input Component
 */
export const Input = ({ label, error, className = '', ...props }) => (
  <div className="mb-4">
    {label && (
      <label className="block text-sm font-medium text-gray-300 mb-2">
        {label}
      </label>
    )}
    <input
      className={classNames(
        'w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition',
        error && 'border-red-500 focus:ring-red-500',
        className
      )}
      {...props}
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

/**
 * Modal Component
 */
export const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  footer = null,
}) => {
  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 px-4 py-4 sm:py-6">
        <div
          className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-full sm:max-w-3xl mx-auto overflow-hidden shadow-xl"
          onClick={(e) => e.stopPropagation()}
          style={{ maxHeight: 'calc(100vh - 40px)' }}
        >
          {/* Header */}
          <div className="border-b border-gray-700 px-6 py-4">
            <h3 className="text-lg font-bold text-white">{title}</h3>
          </div>

          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 170px)' }}>
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="border-t border-gray-700 px-6 py-4 flex flex-col gap-3 sm:flex-row sm:justify-end sm:space-x-3">
              {footer}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

/**
 * StatCard Component
 */
export const StatCard = ({ label, value, icon: Icon, trend = null }) => (
  <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-400 text-sm mb-2">{label}</p>
        <p className="text-2xl font-bold text-white">{value}</p>
        {trend && (
          <p
            className={`text-xs mt-2 ${
              trend > 0 ? 'text-green-400' : 'text-red-400'
            }`}
          >
            {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}% from last week
          </p>
        )}
      </div>
      {Icon && (
        <div className="p-3 bg-purple-600 bg-opacity-20 rounded-lg">
          <Icon size={28} className="text-purple-400" />
        </div>
      )}
    </div>
  </div>
);

/**
 * Badge Component
 */
export const Badge = ({ children, variant = 'default', className = '' }) => {
  const variants = {
    default: 'bg-gray-700 text-gray-100',
    success: 'bg-green-900 text-green-200',
    danger: 'bg-red-900 text-red-200',
    warning: 'bg-yellow-900 text-yellow-200',
    info: 'bg-blue-900 text-blue-200',
  };

  return (
    <span
      className={classNames(
        'px-3 py-1 rounded-full text-xs font-medium',
        variants[variant],
        className
      )}
    >
      {children}
    </span>
  );
};

/**
 * Loading Skeleton
 */
export const Skeleton = ({ className = '' }) => (
  <div
    className={classNames(
      'bg-gray-700 rounded animate-pulse',
      className
    )}
  ></div>
);

/**
 * Alert Component
 */
export const Alert = ({ type = 'info', message, onClose = null }) => {
  const typeClasses = {
    info: 'bg-blue-900 border-blue-700 text-blue-200',
    success: 'bg-green-900 border-green-700 text-green-200',
    warning: 'bg-yellow-900 border-yellow-700 text-yellow-200',
    error: 'bg-red-900 border-red-700 text-red-200',
  };

  return (
    <div className={classNames(
      'border rounded-lg p-4 mb-4',
      typeClasses[type]
    )}>
      <div className="flex items-center justify-between">
        <span>{message}</span>
        {onClose && (
          <button
            onClick={onClose}
            className="text-lg font-semibold opacity-70 hover:opacity-100"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};
